import { ExternalLink, Heart, PiggyBank } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

interface DonationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  displayName: string
  paypalEmail: string | null
  paypalLink: string | null
  message: string | null
}

export default function DonationDialog({
  open,
  onOpenChange,
  displayName,
  paypalEmail,
  paypalLink,
  message,
}: DonationDialogProps) {
  const isPayPalMeLink = (link: string) =>
    /^https?:\/\/(www\.)?paypal\.me\//i.test(link)

  const buildPayPalUrl = () => {
    if (paypalEmail && isPayPalMeLink(paypalEmail)) {
      return paypalEmail
    }
    if (paypalLink && isPayPalMeLink(paypalLink)) {
      return paypalLink
    }
    if (paypalEmail && /^https?:\/\//i.test(paypalEmail)) {
      return paypalEmail
    }
    return null
  }

  const alternativeLink = paypalLink && !isPayPalMeLink(paypalLink) ? paypalLink : null

  const openPayPal = () => {
    const url = buildPayPalUrl()
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const openAlternative = () => {
    if (alternativeLink) window.open(alternativeLink, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-md max-h-[90vh] overflow-y-auto glass-strong">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-neutral-900 flex items-center gap-2">
            <PiggyBank className="h-6 w-6 text-pink-500" />
            Fai una donazione
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            Anziché comprare un articolo della lista, puoi fare una libera donazione a {displayName}.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          {message && (
            <div className="rounded-xl bg-pink-50 border border-pink-100 p-4">
              <p className="text-sm text-neutral-700 italic">&ldquo;{message}&rdquo;</p>
            </div>
          )}

          {buildPayPalUrl() && (
            <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-4">
              <p className="text-xs text-neutral-500 leading-relaxed">
                Verrai reindirizzato a PayPal per completare la donazione in modo sicuro.
                Make me a Gift non trattiene alcuna commissione.
              </p>
            </div>
          )}

          {buildPayPalUrl() && (
            <button
              type="button"
              onClick={openPayPal}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-neutral-800"
            >
              <Heart className="h-4 w-4" />
              Dona su PayPal
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </button>
          )}

          {alternativeLink && (
            <button
              type="button"
              onClick={openAlternative}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-neutral-200 bg-white px-5 py-3 text-sm font-semibold text-neutral-900 transition hover:border-neutral-400 hover:bg-neutral-50"
            >
              <Heart className="h-4 w-4 text-pink-500" />
              Dona con un altro servizio
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
